import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff, Star, Heart, Clock, Lock } from 'lucide-react';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success('Přihlášení úspěšné');
      navigate('/admin');
    } catch (error) {
      const message = error.response?.data?.detail || 'Přihlášení se nezdařilo';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFFDF7] via-[#E8F4FD] to-[#E6FFED] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 left-20 w-20 h-20 bg-[#FF6B6B]/20 rounded-full animate-float" />
      <div className="absolute bottom-20 right-20 w-16 h-16 bg-[#45AAF2]/20 rounded-full animate-bounce-slow" />
      <Star className="absolute top-32 right-1/4 text-[#FECA57] animate-wiggle" size={24} fill="#FECA57" />
      <Heart className="absolute bottom-1/4 left-1/4 text-[#FF6B6B] animate-bounce-slow" size={20} fill="#FF6B6B" />

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#45AAF2] font-semibold mb-6">
            <ArrowLeft size={18} />
            Zpět na rozcestník
          </Link>
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#45AAF2] to-[#26DE81] flex items-center justify-center text-3xl mx-auto mb-4 shadow-lg">
            🔐
          </div>
          <h1 className="text-3xl font-bold text-[#2D3748]">Přihlášení</h1>
          <p className="text-[#64748B] mt-2">Do administrace webu</p>
        </div>

        <div className="bg-white rounded-3xl border-4 border-[#F0F4F8] p-8 shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-bold">E-mail</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-xl h-12 border-2"
                placeholder="vas@email.cz"
                data-testid="login-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="font-bold">Heslo</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="rounded-xl h-12 pr-12 border-2"
                  placeholder="••••••••"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#2D3748]"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full rounded-full h-12 text-lg font-bold bg-gradient-to-r from-[#45AAF2] to-[#26DE81] hover:opacity-90"
              data-testid="login-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Přihlašování...
                </span>
              ) : (
                'Přihlásit se'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/admin/zapomenute-heslo" className="text-sm text-[#45AAF2] hover:underline font-semibold">
              Zapomenuté heslo?
            </Link>
          </div>

          <div className="mt-6 pt-6 border-t-2 border-[#F0F4F8] text-center">
            <span className="text-sm text-[#64748B]">Nemáte účet? </span>
            <Link to="/admin/registrace" className="text-sm text-[#FF6B6B] hover:underline font-bold">
              Registrovat se
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [registrationClosed, setRegistrationClosed] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/settings/registration`)
      .then(res => {
        if (!res.data.is_enabled) setRegistrationClosed(true);
      })
      .catch(() => {})
      .finally(() => setCheckingStatus(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Hesla se neshodují');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Heslo musí mít alespoň 6 znaků');
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await register(name, email, password);
      if (result.pending_approval) {
        setPendingApproval(true);
      } else {
        toast.success('Registrace úspěšná');
        navigate('/admin');
      }
    } catch (error) {
      const message = error.response?.data?.detail || error.response?.data?.message || 'Registrace se nezdařila';
      if (message.includes('čeká na schválení') || error.response?.data?.pending_approval) {
        setPendingApproval(true);
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#FFFDF7] via-[#FFE6E6] to-[#E6FFED]">
        <div className="spinner" />
      </div>
    );
  }

  if (registrationClosed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FFFDF7] via-[#FFE6E6] to-[#E6FFED] flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-3xl border-4 border-[#F0F4F8] p-8 shadow-xl">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#FF9F43] flex items-center justify-center mx-auto mb-6">
              <Lock size={40} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold text-[#2D3748] mb-4" data-testid="registration-closed-title">Registrace uzavreny</h2>
            <p className="text-[#64748B] mb-6">
              Registrace nových uživatelu jsou momentálne uzavreny. 
              Pokud potrebujete prístup, kontaktujte administrátora.
            </p>
            <div className="space-y-3">
              <Link to="/admin/login">
                <Button variant="outline" className="w-full rounded-full border-2 border-[#45AAF2] text-[#45AAF2] hover:bg-[#45AAF2]/10" data-testid="back-to-login-btn">
                  Zpet na prihlášení
                </Button>
              </Link>
              <Link to="/">
                <Button variant="ghost" className="w-full rounded-full text-[#64748B]">
                  Na hlavní stránku
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (pendingApproval) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FFFDF7] via-[#FFF5E6] to-[#FFEAA7] flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-3xl border-4 border-[#FECA57] p-8 shadow-xl">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#FECA57] to-[#FF9F43] flex items-center justify-center mx-auto mb-6">
              <Clock size={40} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold text-[#2D3748] mb-4">Čeká na schválení</h2>
            <p className="text-[#64748B] mb-6">
              Váš účet byl úspěšně vytvořen a čeká na schválení administrátorem. 
              Jakmile bude váš účet schválen, budete se moci přihlásit.
            </p>
            <div className="space-y-3">
              <Link to="/admin/login">
                <Button variant="outline" className="w-full rounded-full border-2 border-[#FECA57] text-[#FF9F43] hover:bg-[#FECA57]/10">
                  Zpět na přihlášení
                </Button>
              </Link>
              <Link to="/">
                <Button variant="ghost" className="w-full rounded-full text-[#64748B]">
                  Na hlavní stránku
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFFDF7] via-[#FFE6E6] to-[#E6FFED] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 right-20 w-20 h-20 bg-[#26DE81]/20 rounded-full animate-float" />
      <div className="absolute bottom-20 left-20 w-16 h-16 bg-[#FF6B6B]/20 rounded-full animate-bounce-slow" />

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#45AAF2] font-semibold mb-6">
            <ArrowLeft size={18} />
            Zpět na rozcestník
          </Link>
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#FF6B6B] to-[#FF9F43] flex items-center justify-center text-3xl mx-auto mb-4 shadow-lg">
            ✨
          </div>
          <h1 className="text-3xl font-bold text-[#2D3748]">Registrace</h1>
          <p className="text-[#64748B] mt-2">Vytvořte si účet</p>
        </div>

        <div className="bg-white rounded-3xl border-4 border-[#F0F4F8] p-8 shadow-xl">
          {/* Info box */}
          <div className="bg-gradient-to-r from-[#FFF5E6] to-[#FFEAA7] rounded-xl p-4 mb-6 border-2 border-[#FECA57]/30">
            <p className="text-sm text-[#92400E]">
              <strong>📋 Poznámka:</strong> Po registraci musí váš účet schválit administrátor.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="font-bold">Jméno a příjmení</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="rounded-xl h-12 border-2"
                placeholder="Jan Novák"
                data-testid="register-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="font-bold">E-mail</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-xl h-12 border-2"
                placeholder="vas@email.cz"
                data-testid="register-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="font-bold">Heslo</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="rounded-xl h-12 pr-12 border-2"
                  placeholder="Minimálně 6 znaků"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="font-bold">Potvrzení hesla</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="rounded-xl h-12 border-2"
                placeholder="Zopakujte heslo"
                data-testid="register-confirm-password"
              />
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full rounded-full h-12 text-lg font-bold bg-gradient-to-r from-[#FF6B6B] to-[#FF9F43] hover:opacity-90"
              data-testid="register-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Registrace...
                </span>
              ) : (
                'Registrovat se'
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t-2 border-[#F0F4F8] text-center">
            <span className="text-sm text-[#64748B]">Již máte účet? </span>
            <Link to="/admin/login" className="text-sm text-[#45AAF2] hover:underline font-bold">
              Přihlásit se
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const { authAPI } = await import('../../lib/api');
      await authAPI.requestPasswordReset(email);
      setSubmitted(true);
    } catch (error) {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFFDF7] via-[#E8F4FD] to-[#F3E6FF] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/admin/login" className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#45AAF2] font-semibold mb-6">
            <ArrowLeft size={18} />
            Zpět na přihlášení
          </Link>
          <h1 className="text-3xl font-bold text-[#2D3748]">Zapomenuté heslo</h1>
          <p className="text-[#64748B] mt-2">Zadejte svůj email</p>
        </div>

        <div className="bg-white rounded-3xl border-4 border-[#F0F4F8] p-8 shadow-xl">
          {submitted ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#45AAF2] to-[#26DE81] flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#2D3748] mb-2">Email odeslán</h3>
              <p className="text-[#64748B]">
                Pokud email existuje, obdržíte instrukce pro obnovu hesla.
              </p>
              <div className="mt-4 p-3 bg-[#FFF5E6] rounded-xl border-2 border-[#FECA57]/30">
                <p className="text-sm text-[#92400E]">
                  <strong>Demo režim:</strong> Emaily se neodesílají.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="font-bold">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="rounded-xl h-12 border-2"
                  placeholder="vas@email.cz"
                  data-testid="forgot-email"
                />
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full rounded-full h-12 text-lg font-bold bg-gradient-to-r from-[#A55EEA] to-[#45AAF2] hover:opacity-90"
                data-testid="forgot-submit"
              >
                {loading ? 'Odesílání...' : 'Obnovit heslo'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
